import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MyOrdersPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/prihlaseni'); return; }
    axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOrders(res.data)).catch(console.error).finally(() => setLoading(false));
  }, [user, token, navigate, authLoading]);

  if (authLoading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>;
  if (!user) return null;

  const statusLabels = {
    pending: { label: 'Ceka na zpracovani', color: 'bg-amber-100 text-amber-700' },
    confirmed: { label: 'Potvrzeno', color: 'bg-blue-100 text-blue-700' },
    processing: { label: 'Zpracovava se', color: 'bg-purple-100 text-purple-700' },
    shipped: { label: 'Odeslano', color: 'bg-cyan-100 text-cyan-700' },
    delivered: { label: 'Doruceno', color: 'bg-emerald-100 text-emerald-700' },
    cancelled: { label: 'Zruseno', color: 'bg-red-100 text-red-700' }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="my-orders-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Moje objednavky</h1>
      {loading ? (
        <div className="flex items-center justify-center min-h-[200px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-8 text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" /><p className="text-slate-500 mb-4">Zatim nemate zadne objednavky</p>
          <Link to="/produkty" className="inline-block bg-[#004E98] text-white hover:bg-[#003B73] rounded-sm px-6 py-3 font-medium">Prohlednout produkty</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div><p className="font-bold text-[#004E98]">{order.order_number}</p><p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleDateString('cs-CZ')}</p></div>
                <span className={`px-3 py-1 text-sm font-medium rounded ${statusLabels[order.status]?.color || 'bg-slate-100'}`}>{statusLabels[order.status]?.label || order.status}</span>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <div className="text-sm text-slate-600">
                  {order.items?.slice(0, 3).map((item, i) => (<span key={i}>{item.name}{i < Math.min(order.items.length, 3) - 1 ? ', ' : ''}</span>))}
                  {order.items?.length > 3 && <span className="text-slate-400"> +{order.items.length - 3} dalsich</span>}
                </div>
                <div className="flex justify-between items-center mt-3">
                  <span className="font-bold text-lg">{order.total?.toFixed(2)} Kc</span>
                  <Link to={`/objednavka/${order.id}`} className="text-[#004E98] hover:underline text-sm">Detail objednavky &#8594;</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
